import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { brandLogo, brandName } from './branding';
import { resolveImageUrl } from './imageUrl';

const C = {
    green: [46, 125, 50],
    greenLight: [232, 245, 233],
    green50: [241, 248, 233],
    blue: [66, 133, 244],
    blueLight: [227, 242, 253],
    orange: [255, 152, 0],
    orangeLight: [255, 243, 224],
    orange50: [255, 248, 225],
    dark: [33, 37, 41],
    gray: [108, 117, 125],
    grayLight: [173, 181, 189],
    bg: [248, 249, 250],
    white: [255, 255, 255],
    divider: [222, 226, 230],
    black: [0, 0, 0],
};

function rr(doc, x, y, w, h, r, color) {
    doc.setFillColor(...color);
    doc.roundedRect(x, y, w, h, r, r, 'F');
}

function imageToCanvas(img) {
    const canvas = document.createElement('canvas');
    const maxW = 800;
    const maxH = 800;
    let w = img.width;
    let h = img.height;
    if (w > maxW) { h = h * (maxW / w); w = maxW; }
    if (h > maxH) { w = w * (maxH / h); h = maxH; }
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    return { data: canvas.toDataURL('image/jpeg', 0.85), ratio: img.height / img.width };
}

async function loadImageViaFetch(url) {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const img = new Image();
            img.onload = () => resolve(imageToCanvas(img));
            img.onerror = () => resolve(null);
            img.src = reader.result;
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
    });
}

function loadImageViaImg(url) {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try { resolve(imageToCanvas(img)); }
            catch { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

function base64ImageRatio(dataUrl) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.height / img.width);
        img.onerror = () => resolve(0.5);
        img.src = dataUrl;
    });
}

async function loadImageAsBase64(url) {
    // First try direct fetch
    try {
        const result = await loadImageViaFetch(url);
        if (result) return result;
    } catch { /* CORS failed */ }

    // Try img tag fallback
    try {
        const result = await loadImageViaImg(url);
        if (result) return result;
    } catch { /* also failed */ }

    // Use server proxy for static.souschaif.com images
    if (url.includes('static.souschaif.com')) {
        try {
            const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(url)}`;
            const result = await loadImageViaFetch(proxyUrl);
            if (result) return result;
        } catch { /* proxy failed too */ }
    }

    return null; // returns { data, ratio } or null
}

function loadImgElement(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

function getImageUrl(recipe, imageData) {
    return resolveImageUrl(imageData) || resolveImageUrl(recipe?.image_path) || null;
}

export async function generateRecipePdf(recipe, t, imageData) {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    let y = 0;

    const safe = {
        ...recipe,
        ingredients: recipe?.ingredients || [],
        instructions: recipe?.instructions || '',
        name: recipe?.name || recipe?.strMeal || 'Recipe',
        intro: recipe?.intro || '',
        nutritions: recipe?.nutritions || {},
        calories_per_serving: recipe?.calories_per_serving || 0,
        cooking_time: recipe?.cooking_time || 0,
        servings: recipe?.servings || 1,
    };

    // ── HEADER BAR ──
    const headerH = 18;
    rr(doc, 0, 0, pageW, headerH, 0, C.white);
    doc.setDrawColor(...C.divider);
    doc.setLineWidth(0.3);
    doc.line(0, headerH, pageW, headerH);

    // Logo
    try {
        const logoImg = await loadImgElement(brandLogo);
        if (logoImg) {
            const canvas = document.createElement('canvas');
            canvas.width = logoImg.width;
            canvas.height = logoImg.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(logoImg, 0, 0);
            const logoData = canvas.toDataURL('image/png');
            const logoH = 12;
            const logoW = (logoImg.width / logoImg.height) * logoH;
            doc.addImage(logoData, 'PNG', margin, (headerH - logoH) / 2, logoW, logoH);
        }
    } catch { /* logo not available */ }

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.grayLight);
    doc.text(`${brandName.toLowerCase()}.com`, pageW - margin, headerH / 2 + 1, { align: 'right' });

    y = headerH + 8;

    // ── RECIPE IMAGE ──
    const imgUrl = getImageUrl(safe, imageData);
    let hasImage = false;
    if (imgUrl) {
        try {
            let imgData, imgRatio;

            if (imgUrl.startsWith('data:')) {
                imgData = imgUrl;
                imgRatio = await base64ImageRatio(imgUrl);
            } else {
                const result = await loadImageAsBase64(imgUrl);
                if (result) {
                    imgData = result.data;
                    imgRatio = result.ratio;
                }
            }

            if (imgData) {
                const maxImgH = 100;
                const imgW = Math.min(contentW, maxImgH / imgRatio);
                const imgH = imgW * imgRatio;
                const imgX = margin + (contentW - imgW) / 2;

                doc.addImage(imgData, 'JPEG', imgX, y, imgW, imgH);
                doc.setDrawColor(...C.divider);
                doc.setLineWidth(0.3);
                doc.roundedRect(imgX, y, imgW, imgH, 2, 2, 'S');
                y += imgH + 6;
                hasImage = true;
            }
        } catch { /* skip */ }
    }

    // ── RECIPE NAME ──
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    const titleLines = doc.splitTextToSize(safe.name, contentW);
    doc.text(titleLines, margin, y + 7);
    y += titleLines.length * 9 + 4;

    // ── GREEN ACCENT LINE ──
    doc.setFillColor(...C.green);
    doc.rect(margin, y, 40, 1, 'F');
    y += 6;

    // ── META INFO (matching the popup: blue clock, green people, orange fire) ──
    const metaBoxW = (contentW - 8) / 3;
    const metaH = 16;

    // Cooking time - blue tint
    rr(doc, margin, y, metaBoxW, metaH, 3, C.blueLight);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.blue);
    doc.text(t?.cookingTime || 'Cooking time', margin + metaBoxW / 2, y + 5.5, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text(`${safe.cooking_time} ${t?.minutes || 'min'}`, margin + metaBoxW / 2, y + 12, { align: 'center' });

    // Servings - green tint
    const col2X = margin + metaBoxW + 4;
    rr(doc, col2X, y, metaBoxW, metaH, 3, C.greenLight);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.green);
    doc.text(t?.servings || 'Servings', col2X + metaBoxW / 2, y + 5.5, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text(`${safe.servings}`, col2X + metaBoxW / 2, y + 12, { align: 'center' });

    // Calories - orange tint
    const col3X = margin + (metaBoxW + 4) * 2;
    rr(doc, col3X, y, metaBoxW, metaH, 3, C.orangeLight);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.orange);
    doc.text(t?.calories || 'Calories', col3X + metaBoxW / 2, y + 5.5, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text(`${safe.calories_per_serving} kcal`, col3X + metaBoxW / 2, y + 12, { align: 'center' });

    y += metaH + 8;

    // ── INTRO ──
    if (safe.intro) {
        rr(doc, margin, y, contentW, 0, 2, C.bg);
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...C.gray);
        const introLines = doc.splitTextToSize(safe.intro, contentW - 12);
        const introH = introLines.length * 4.5 + 10;
        rr(doc, margin, y, contentW, introH, 3, C.bg);
        doc.text(introLines, margin + 6, y + 7);
        y += introH + 6;
    }

    const checkPage = (needed) => {
        if (y + needed > pageH - 20) {
            doc.addPage();
            y = margin;
            return true;
        }
        return false;
    };

    // ── INGREDIENTS (orange boxes like the popup) ──
    checkPage(20);
    doc.setFillColor(...C.orange);
    doc.roundedRect(margin, y, 3, 7, 1, 1, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text(t?.ingredients || 'Ingredients', margin + 7, y + 5.5);
    y += 12;

    if (safe.ingredients && safe.ingredients.length > 0) {
        const cols = 3;
        const gap = 3;
        const cellW = (contentW - gap * (cols - 1)) / cols;
        const cellH = 14;

        for (let i = 0; i < safe.ingredients.length; i++) {
            const col = i % cols;
            const ingredient = safe.ingredients[i];

            if (col === 0) checkPage(cellH + 3);

            const bx = margin + col * (cellW + gap);
            rr(doc, bx, y, cellW, cellH, 2, C.orange50);

            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...C.orange);
            const name = ingredient.name || String(ingredient);
            const truncName = name.length > 18 ? name.substring(0, 17) + '…' : name;
            doc.text(truncName, bx + cellW / 2, y + 5.5, { align: 'center' });

            if (ingredient.quantity) {
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...C.gray);
                doc.setFontSize(7);
                const qty = String(ingredient.quantity);
                const truncQty = qty.length > 20 ? qty.substring(0, 19) + '…' : qty;
                doc.text(truncQty, bx + cellW / 2, y + 10.5, { align: 'center' });
            }

            if (col === cols - 1 || i === safe.ingredients.length - 1) {
                y += cellH + 2;
            }
        }
        y += 4;
    }

    // ── INSTRUCTIONS ──
    checkPage(20);
    doc.setFillColor(...C.green);
    doc.roundedRect(margin, y, 3, 7, 1, 1, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.dark);
    doc.text(t?.instructions || 'Instructions', margin + 7, y + 5.5);
    y += 12;

    const instructions = Array.isArray(safe.instructions)
        ? safe.instructions
        : typeof safe.instructions === 'string'
            ? safe.instructions.split('\n').filter(s => s.trim())
            : [];

    instructions.forEach((step, idx) => {
        const text = String(step).trim();
        if (!text) return;

        doc.setFontSize(9);
        const wrapped = doc.splitTextToSize(text, contentW - 16);
        const blockH = wrapped.length * 4.2 + 6;

        checkPage(blockH + 4);

        // Step number circle (green like the app)
        const cx = margin + 4;
        const cy = y + 4;
        doc.setFillColor(...C.green);
        doc.circle(cx, cy, 3.5, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...C.white);
        doc.text(String(idx + 1), cx, cy + 1, { align: 'center' });

        // Step text
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...C.dark);
        doc.setFontSize(9);
        doc.text(wrapped, margin + 12, y + 4);

        y += blockH;

        if (idx < instructions.length - 1) {
            doc.setDrawColor(...C.divider);
            doc.setLineWidth(0.15);
            doc.line(margin + 12, y - 1, margin + contentW, y - 1);
        }
    });

    y += 6;

    // ── NUTRITION TABLE ──
    if (safe.nutritions && (Array.isArray(safe.nutritions) ? safe.nutritions.length > 0 : Object.keys(safe.nutritions).length > 0)) {
        checkPage(30);

        doc.setFillColor(...C.blue);
        doc.roundedRect(margin, y, 3, 7, 1, 1, 'F');
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...C.dark);
        doc.text(t?.nutritionalInfo || 'Nutrition', margin + 7, y + 5.5);
        y += 12;

        const nutritions = Array.isArray(safe.nutritions)
            ? safe.nutritions
            : Object.entries(safe.nutritions).map(([name, quantity]) => ({ name, quantity }));

        if (nutritions.length > 0) {
            const rows = nutritions.map(n => [n.name || '', String(n.quantity || '')]);
            autoTable(doc, {
                startY: y,
                head: [[t?.nutrient || 'Nutrient', t?.amount || 'Amount']],
                body: rows,
                margin: { left: margin, right: margin },
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    textColor: C.dark,
                    lineWidth: 0,
                },
                headStyles: {
                    fillColor: C.green,
                    textColor: C.white,
                    fontStyle: 'bold',
                    fontSize: 9,
                },
                alternateRowStyles: {
                    fillColor: C.greenLight,
                },
                columnStyles: {
                    0: { cellWidth: contentW * 0.6 },
                    1: { cellWidth: contentW * 0.4, halign: 'right' },
                },
            });
            y = (doc.lastAutoTable?.finalY || y) + 8;
        }
    }

    // ── FOOTER on every page ──
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const fY = pageH - 10;

        // Divider line
        doc.setDrawColor(...C.divider);
        doc.setLineWidth(0.3);
        doc.line(margin, fY, pageW - margin, fY);

        // Brand name left
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...C.grayLight);
        doc.text(`${brandName}`, margin, fY + 5);

        // Recipe name center
        doc.setTextColor(...C.gray);
        const footerTitle = safe.name.length > 50 ? safe.name.substring(0, 47) + '...' : safe.name;
        doc.text(footerTitle, pageW / 2, fY + 5, { align: 'center' });

        // Page number right
        doc.setTextColor(...C.grayLight);
        doc.text(`${i} / ${totalPages}`, pageW - margin, fY + 5, { align: 'right' });
    }

    const fileName = safe.name
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50);
    doc.save(`${fileName}_recipe.pdf`);
}
